import {
  InterpolationError,
  extractRootNames,
  getByPath,
  hasTemplate,
  interpolate,
  parse,
} from '../interpolation';
import { markPublishedScopeContext } from '../published-scope/unpublishedScope';
import { resetWarnOnce, warnOnce } from '../warnOnce';
import type { PathSegment } from '../interpolation/types';
import type {
  DataSourceStatus,
  ResolvedDataSource,
} from '../../providers/DataSourceManager.types';
import {
  ACTION_PREFIX,
  BADGE_FIELD,
  BIND_PREFIX,
  DEFAULT_TAG_KEYS_FIELD,
  AUTO_SELECT_FIRST_OPTION_PROP,
  FORM_KEY_PROP,
  KEEP_OPTIONS_WHILE_LOADING_PROP,
  META_PREFIX,
  OPTION_LABEL_FIELD,
  OPTION_VALUE_FIELD,
  STAT_LABEL_FIELD,
  STAT_TOOLTIP_FIELD,
  STAT_VALUE_FIELD,
  TAG_KEYS_FIELD,
} from '../binding-enhancer/constants';
import type { BindTransform } from '../binding-enhancer/types';
import {
  coerceFieldCheckResult,
  evaluateFieldCheck,
} from '../binding-enhancer/field-check';
import {
  evaluateVisibilityWhen,
  parseVisibilityWhen,
} from '../binding-enhancer/visibility';
import type { FormState } from '@de/providers/DataSourceManager.types';
import type { CeRoute } from '../../providers/RouteProvider';
import { isBindDeclaration } from './isBindDeclaration';
import { collectBindTargetPropNames } from './collectBindTargetPropNames';
import { resolvePlainPropValue } from './resolvePlainPropValue';
import {
  RESERVED_BIND_SOURCES,
  activeProviderRoots,
  isReservedInterpolationRoot,
} from './reservedRoots';
import { rootForSource } from './rootForSource';
import {
  applyBadges,
  applyCount,
  applyIsEmpty,
  applyOptions,
  applyStats,
  applyTagLabels,
  applyText,
  type TransformCompanions,
} from './transforms';

/**
 * Runtime context handed to {@link resolveBindings}. Mirrors the DSM
 * interpolation root (`{ context: flowContext, formState, ...sources }`,
 * see `DataSourceManager.tsx#buildContextFromLiveData`) plus the optional
 * repeater-scope `item` provided by {@link useItemScope} (ADR-13). This
 * keeps a single template grammar across `dataSources[*].query`,
 * `_bind.*`, and `_action.*.params`:
 *
 *   - `{{context.<key>}}`   → host-supplied `flowContext`
 *   - `{{formState.<key>}}` → DSM-managed form state
 *   - `{{item.<path>}}`     → repeater-scope item (when in scope)
 *   - `{{route.<path>}}`    → host-injected route
 *   - `{{scope.<key>}}`     → nearest published scope bag
 *   - `{{<sourceName>...}}` → resolved data source payload
 */
export interface BindingContext {
  readonly sources: Readonly<Record<string, ResolvedDataSource>>;
  readonly item?: unknown;
  readonly formState: FormState;
  readonly flowContext: Readonly<Record<string, unknown>>;
  /** Injected route. Absent outside a `RouteProvider`. */
  readonly route?: CeRoute;
  /**
   * Nearest published scope. An empty object means every key is
   * unpublished. A key set to `null` is published.
   */
  readonly scope?: Readonly<Record<string, unknown>>;
}

/**
 * Output of {@link resolveBindings}. `props` is the stripped, resolved
 * prop bag ready to be merged with action handlers and passed to the
 * HEN UI component. `loading` is the union of every referenced source's
 * `pending` state. `dataSourceStatus` is the aggregate worst-case lifecycle
 * state across the same referenced sources (`error` → `pending` → `empty` →
 * `resolved`). `formKey` is the literal `formKey` prop value (if any)
 * so `createBoundRender` can wire form-state updates. `meta` is the
 * opaque `_meta.*` payload forwarded to DB-1.5 consumers.
 *
 * `loading` is **not** `dataSourceStatus === 'pending'`, and the difference is
 * load-bearing rather than incidental. They answer different questions: is
 * anything still in flight, versus what should this component draw. Because
 * the aggregate ranks `error` above `pending`, a component reading a failed
 * source and an unsettled one reports `loading: true` with an `error` status,
 * and an unregistered source contributes `error` while contributing nothing to
 * `loading`. Collapsing either into the other loses a state the UI can
 * legitimately be in; see the divergence cases in `resolveBindings.test.ts`.
 */
export interface ResolvedBindings {
  readonly props: Record<string, unknown>;
  readonly loading: boolean;
  readonly dataSourceStatus: DataSourceStatus;
  readonly formKey?: string;
  /**
   * Authored opt-in for committing the first resolved option into an empty
   * `formKey`. Reserved rather than forwarded, so the component never sees it.
   */
  readonly autoSelectFirstOption: boolean;
  /**
   * Authored opt-in for keeping a MultiSelect's current options while a
   * refresh is in flight. Reserved rather than forwarded.
   */
  readonly keepOptionsWhileLoading: boolean;
  readonly meta: Record<string, unknown>;
  /**
   * Names of every data source this component reads from across all
   * `_bind.*` declarations and `_action.*.params` templates. Surfaced
   * so the Render boundary can subscribe to the right slice of DSM
   * state for behaviours that fire on source-settle transitions
   * (e.g. the synthetic `onSourceChange` event the cascading-select
   * reset pattern uses) without re-walking the prop bag. Stable
   * enough for `useEffect` deps — {@link resolveBindings} produces
   * the same set membership for the same input prop bag (insertion
   * order may differ; consumers that need a stable string
   * fingerprint should sort).
   */
  readonly referencedSources: ReadonlySet<string>;
}

/**
 * Parsed-path cache (module-scope, bounded). `parse()` is not free —
 * memoizing per distinct path keeps the walker at ~`O(numProps)` even
 * for large DSLs. Bounded at 1000 entries with FIFO eviction; any
 * reasonable config lives well under this ceiling.
 */
const PATH_CACHE_LIMIT = 1000;
const pathCache = new Map<string, ReadonlyArray<PathSegment>>();

function cachedParsePath(path: string): ReadonlyArray<PathSegment> {
  const cached = pathCache.get(path);
  if (cached !== undefined) {
    return cached;
  }
  let parsed: ReadonlyArray<PathSegment>;
  try {
    const { segments } = parse(`{{${path}}}`);
    const first = segments[0];
    parsed =
      first && first.type === 'expression' && first.kind === 'path'
        ? first.segments
        : [];
  } catch (err) {
    if (!(err instanceof InterpolationError)) {
      throw err;
    }
    warnOnce(
      WARN_ONCE_NAMESPACE,
      `[binding-enhancer] Skipping invalid bind declaration path "${path}": ${err.message}`,
    );
    parsed = [];
  }
  if (pathCache.size >= PATH_CACHE_LIMIT) {
    const firstKey = pathCache.keys().next().value;
    if (firstKey !== undefined) {
      pathCache.delete(firstKey);
    }
  }
  pathCache.set(path, parsed);
  return parsed;
}

/**
 * Traverse `rawProps` once and produce a {@link ResolvedBindings}
 * snapshot.
 *
 * See child SAD §5.2 and the plan §3.3 tables for per-key semantics.
 * Summary:
 *   - `_bind.<prop>` with a well-formed {@link BindDeclaration}:
 *        resolve → optional transform → emit `props[prop]`.
 *   - `_bind.<prop>` with a string containing `{{…}}`:
 *        interpolate against the DSM-shaped interpolation context
 *        (`{ context: flowContext, formState, item, ...sourceData }`)
 *        and emit.
 *   - `_bind.optionLabelField` / `_bind.optionValueField` /
 *     `_bind.tagKeys` / `_bind.defaultTagKeys`:
 *        collected first, consumed by `options` / `tagLabels`
 *        transforms on the same component. The tag-key companions may
 *        themselves be bindings / templates (e.g. `tagKeys` bound to the
 *        active `formState` filter selection).
 *   - `_action.<event>`: left alone — {@link resolveActions} handles it.
 *   - `_meta.<key>`: copied into `meta[key]` and stripped.
 *   - `formKey` (plain, string): captured into `formKey` and stripped.
 *   - `autoSelectFirstOption` (plain, boolean): captured into
 *     `autoSelectFirstOption` and stripped.
 *   - `keepOptionsWhileLoading` (plain, boolean): captured into
 *     `keepOptionsWhileLoading` and stripped.
 *   - anything else: plain prop — interpolated when it contains `{{…}}`
 *     (unless a sibling `_bind.<prop>` exists for the same name).
 *
 * All internal-prefixed props are stripped unconditionally, regardless
 * of whether the resolution step produced a value — this satisfies
 * §11 (Security Model): HEN UI never receives a `_bind.` / `_action.`
 * / `_meta.` key.
 */
export function resolveBindings(
  rawProps: Readonly<Record<string, unknown>>,
  ctx: BindingContext,
): ResolvedBindings {
  const bindTargetProps = collectBindTargetPropNames(rawProps);
  const outProps: Record<string, unknown> = {};
  const meta: Record<string, unknown> = {};
  const referencedSources = new Set<string>();
  // Resolve companions after `referencedSources` exists so a dynamic
  // `_bind.tagKeys` / `_bind.defaultTagKeys` (e.g. bound to `formState`)
  // can register its source dependency like any other binding.
  const companions = collectCompanions(rawProps, ctx, referencedSources);
  let formKey: string | undefined;
  let autoSelectFirstOption = false;
  let keepOptionsWhileLoading = false;

  for (const key of Object.keys(rawProps)) {
    const value = rawProps[key];

    if (key.startsWith(BIND_PREFIX)) {
      const propName = key.slice(BIND_PREFIX.length);
      if (propName.length === 0) {
        warnOnce(
          WARN_ONCE_NAMESPACE,
          `[binding-enhancer] Ignoring empty bind key "${BIND_PREFIX}" — DSL must declare a target prop name (e.g. "${BIND_PREFIX}label").`,
        );
        continue;
      }
      if (
        propName === OPTION_LABEL_FIELD ||
        propName === OPTION_VALUE_FIELD ||
        propName === TAG_KEYS_FIELD ||
        propName === DEFAULT_TAG_KEYS_FIELD ||
        propName === STAT_LABEL_FIELD ||
        propName === STAT_VALUE_FIELD ||
        propName === STAT_TOOLTIP_FIELD ||
        propName === BADGE_FIELD
      ) {
        continue;
      }
      const resolved = resolveBindProp(
        propName,
        value,
        ctx,
        companions,
        referencedSources,
      );
      outProps[propName] = resolved;
      continue;
    }

    if (key.startsWith(ACTION_PREFIX)) {
      const providerRoots = activeProviderRoots(ctx);
      for (const head of collectTemplatePathsFromAction(value)) {
        if (head && !isReservedInterpolationRoot(head, providerRoots)) {
          referencedSources.add(head);
        }
      }
      continue;
    }

    if (key.startsWith(META_PREFIX)) {
      const metaName = key.slice(META_PREFIX.length);
      if (metaName.length === 0) {
        warnOnce(
          WARN_ONCE_NAMESPACE,
          `[binding-enhancer] Ignoring empty meta key "${META_PREFIX}" — DSL must declare a meta name (e.g. "${META_PREFIX}flowState").`,
        );
        continue;
      }
      meta[metaName] = value;
      continue;
    }

    if (key === FORM_KEY_PROP) {
      if (typeof value === 'string' && value.length > 0) {
        formKey = value;
      }
      continue;
    }

    if (key === AUTO_SELECT_FIRST_OPTION_PROP) {
      // Only a literal `true` opts in. A document carrying the string
      // `"false"` -- which is what a checkbox serialized through a query
      // param produces -- would otherwise turn the behavior on.
      autoSelectFirstOption = value === true;
      continue;
    }

    if (key === KEEP_OPTIONS_WHILE_LOADING_PROP) {
      keepOptionsWhileLoading = value === true;
      continue;
    }

    if (bindTargetProps.has(key)) {
      continue;
    }

    outProps[key] = resolvePlainPropValue(key, value, ctx, referencedSources);
  }

  const loading = isAnyReferencedSourcePending(referencedSources, ctx.sources);
  const dataSourceStatus = aggregateSourceStatus(
    referencedSources,
    ctx.sources,
  );

  return {
    props: outProps,
    loading,
    dataSourceStatus,
    formKey,
    autoSelectFirstOption,
    keepOptionsWhileLoading,
    meta,
    referencedSources,
  };
}

function collectCompanions(
  rawProps: Readonly<Record<string, unknown>>,
  ctx: BindingContext,
  referencedSources: Set<string>,
): TransformCompanions {
  const labelKey = `${BIND_PREFIX}${OPTION_LABEL_FIELD}`;
  const valueKey = `${BIND_PREFIX}${OPTION_VALUE_FIELD}`;
  const tagKeysKey = `${BIND_PREFIX}${TAG_KEYS_FIELD}`;
  const defaultTagKeysKey = `${BIND_PREFIX}${DEFAULT_TAG_KEYS_FIELD}`;
  const statLabelKey = `${BIND_PREFIX}${STAT_LABEL_FIELD}`;
  const statValueKey = `${BIND_PREFIX}${STAT_VALUE_FIELD}`;
  const statTooltipKey = `${BIND_PREFIX}${STAT_TOOLTIP_FIELD}`;
  const badgeKey = `${BIND_PREFIX}${BADGE_FIELD}`;
  return {
    // Option companions are field-name strings, never dynamic — left raw.
    optionLabelField: rawProps[labelKey],
    optionValueField: rawProps[valueKey],
    // Stat / badge companions are field-name strings, never dynamic — left
    // raw for `coerceCompanion` to validate at transform time.
    statLabelField: rawProps[statLabelKey],
    statValueField: rawProps[statValueKey],
    statTooltipField: rawProps[statTooltipKey],
    badgeField: rawProps[badgeKey],
    // Tag companions may be a literal array (left raw for `coerceTagKeys`)
    // OR a binding / `{{…}}` template (resolved here against `ctx`). A
    // resolved value that comes back as a comma-separated string — the
    // shape DSM `formState` uses for a multi-select ToggleGroup — is split
    // into a key array so `_bind.tagKeys` can point at the active filter
    // selection.
    tagKeys: resolveTagKeyCompanion(
      rawProps[tagKeysKey],
      ctx,
      referencedSources,
    ),
    defaultTagKeys: resolveTagKeyCompanion(
      rawProps[defaultTagKeysKey],
      ctx,
      referencedSources,
    ),
  };
}

/**
 * Resolve a tag-key companion (`_bind.tagKeys` / `_bind.defaultTagKeys`).
 *
 * - A {@link BindDeclaration} or `{{…}}` template is resolved against the
 *   binding context (no value transform applied) so the key list can be
 *   sourced from `formState`, an `item`, `context`, or a data source.
 * - A resolved comma-separated string is normalized into a trimmed,
 *   non-empty key array (the DSM `formState` shape for multi-select
 *   pills); an already-array result passes through.
 * - Anything else (a literal array, a plain non-template string, etc.) is
 *   returned unchanged for {@link applyTagLabels}'s `coerceTagKeys` to
 *   interpret exactly as before — preserving the legacy companion
 *   contract.
 */
function resolveTagKeyCompanion(
  raw: unknown,
  ctx: BindingContext,
  referencedSources: Set<string>,
): unknown {
  if (raw === undefined) {
    return undefined;
  }
  if (isBindDeclaration(raw)) {
    if (!RESERVED_BIND_SOURCES.has(raw.source)) {
      referencedSources.add(raw.source);
    }
    const root = rootForSource(raw.source, ctx);
    const segments = cachedParsePath(raw.path);
    const resolved =
      segments.length === 0 ? undefined : getByPath(root, segments);
    return normalizeTagKeyList(resolved);
  }
  if (typeof raw === 'string' && hasTemplate(raw)) {
    const interpContext = buildInterpolationContext(
      ctx,
      referencedSources,
      raw,
    );
    try {
      return normalizeTagKeyList(interpolate(raw, interpContext));
    } catch (err) {
      if (err instanceof InterpolationError) {
        warnOnce(
          WARN_ONCE_NAMESPACE,
          `[binding-enhancer] Skipping invalid template on a tag-key companion: ${err.message}`,
        );
        return undefined;
      }
      throw err;
    }
  }
  return raw;
}

/**
 * Coerce a resolved tag-key companion into a key array. Arrays pass
 * through; a comma-separated string (DSM multi-select form-state shape)
 * is split into trimmed, non-empty keys. Everything else is returned
 * unchanged so `coerceTagKeys` keeps its legacy fallback semantics.
 */
function normalizeTagKeyList(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value;
  }
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((key) => key.trim())
      .filter((key) => key.length > 0);
  }
  return value;
}

function resolveBindProp(
  propName: string,
  value: unknown,
  ctx: BindingContext,
  companions: TransformCompanions,
  referencedSources: Set<string>,
): unknown {
  if (isBindDeclaration(value)) {
    if (!RESERVED_BIND_SOURCES.has(value.source)) {
      referencedSources.add(value.source);
    }
    const root = rootForSource(value.source, ctx);
    const segments = cachedParsePath(value.path);
    // `cachedParsePath` returns `[]` only when `parse()` threw an
    // `InterpolationError` (an empty path is rejected upstream by
    // `isBindDeclaration`). Treat malformed paths as unresolved so we
    // do not silently hand the entire source `data` object back to the
    // component as if the author had bound the root.
    const raw = segments.length === 0 ? undefined : getByPath(root, segments);
    // A per-binding `keys` list overrides the node-level `tagKeys`
    // companion for THIS declaration only, so sibling `stats` bindings on
    // the same node (e.g. RankingCard `stats` vs `details`) can curate
    // independent entry sets from a shared keyed source. Each entry may be
    // a bare field-key string or a `{ key, when }` object whose `when`
    // value predicate gates the key on the binding context (e.g. a
    // grad-only tuition row on `context.degree`) — resolved here to the
    // bare-string array `coerceTagKeys` in the transform still validates.
    // `isBindDeclaration` does not validate `keys`, so guard the array
    // shape here: a non-array (e.g. an accidentally string-authored
    // `keys`) fails OPEN to the node-level `tagKeys` companion + a warn,
    // rather than letting `resolveBindingKeys` iterate string characters
    // and silently drop every curated stat.
    let perBindingCompanions = companions;
    if (value.keys !== undefined) {
      if (Array.isArray(value.keys)) {
        perBindingCompanions = {
          ...companions,
          tagKeys: resolveBindingKeys(value.keys, ctx),
        };
      } else {
        warnOnce(
          WARN_ONCE_NAMESPACE,
          `[binding-resolver] Ignoring non-array _bind.${propName}.keys; using node-level tagKeys.`,
        );
      }
    }
    if (value.operator !== undefined) {
      const checked = evaluateFieldCheck(raw, value);
      if (checked === undefined) {
        warnOnce(
          WARN_ONCE_NAMESPACE,
          `[binding-resolver] Unknown field-check operator on _bind.${propName}; leaving the prop unset.`,
        );
        return undefined;
      }
      return coerceFieldCheckResult(propName, checked);
    }
    return applyTransform(value.transform, raw, perBindingCompanions);
  }

  if (typeof value === 'string' && hasTemplate(value)) {
    const interpContext = buildInterpolationContext(
      ctx,
      referencedSources,
      value,
    );
    try {
      return interpolate(value, interpContext);
    } catch (err) {
      if (err instanceof InterpolationError) {
        warnOnce(
          WARN_ONCE_NAMESPACE,
          `[binding-enhancer] Skipping invalid template on _bind.${propName}: ${err.message}`,
        );
        return undefined;
      }
      throw err;
    }
  }

  return value;
}

/**
 * Resolve a per-binding `_bind.*.keys` curation list into the bare
 * field-key `string[]` the `stats` / `tagLabels` transforms consume.
 *
 * - Bare-string entries pass through unchanged (empty strings dropped).
 * - `{ key, when }` object entries ({@link BindKeyEntry}) carry an
 *   optional value predicate (same shape as `_meta.visibility.when`); the
 *   key is emitted only when `when` resolves truthy against `ctx`. A
 *   `when`-less object always emits its key.
 * - A MALFORMED `when` fails OPEN — the key is kept and a warn is emitted
 *   — mirroring the `_meta.visibility` malformed → visible posture so a
 *   bad predicate never silently drops a row.
 * - Entries lacking a non-empty string `key` (and any other shapes) are
 *   skipped with a warn.
 *
 * `coerceTagKeys` in the transform still validates the returned array, so
 * this stays purely additive over the legacy string-array contract.
 */
function resolveBindingKeys(
  keys: readonly unknown[],
  ctx: BindingContext,
): string[] {
  const out: string[] = [];
  for (const entry of keys) {
    if (typeof entry === 'string') {
      if (entry.length > 0) {
        out.push(entry);
      }
      continue;
    }
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
      warnOnce(
        WARN_ONCE_NAMESPACE,
        `[binding-resolver] Ignoring _bind.*.keys entry that is neither a string nor a { key, when } object.`,
      );
      continue;
    }
    const rec = entry as { key?: unknown; when?: unknown };
    if (typeof rec.key !== 'string' || rec.key.length === 0) {
      warnOnce(
        WARN_ONCE_NAMESPACE,
        `[binding-resolver] Ignoring _bind.*.keys entry without a non-empty string "key".`,
      );
      continue;
    }
    if (rec.when === undefined) {
      out.push(rec.key);
      continue;
    }
    const parsed = parseVisibilityWhen(rec.when);
    // `=== false`, not `!parsed.ok`: WP Engine's headless build runs an
    // older TS that narrows this discriminated union only through an
    // explicit literal comparison, not the `!` negation operator.
    if (parsed.ok === false) {
      warnOnce(
        WARN_ONCE_NAMESPACE,
        `[binding-resolver] Malformed _bind.*.keys[].${parsed.reason}; keeping "${rec.key}".`,
      );
      out.push(rec.key);
      continue;
    }
    if (evaluateVisibilityWhen(parsed.when, ctx)) {
      out.push(rec.key);
    }
  }
  return out;
}

function applyTransform(
  transform: BindTransform | undefined,
  raw: unknown,
  companions: TransformCompanions,
): unknown {
  switch (transform) {
    case 'options':
      return applyOptions(raw, companions);
    case 'text':
      return applyText(raw);
    case 'count':
      return applyCount(raw);
    case 'isEmpty':
      return applyIsEmpty(raw);
    case 'tagLabels':
      return applyTagLabels(raw, companions);
    case 'stats':
      return applyStats(raw, companions);
    case 'badges':
      return applyBadges(raw, companions);
    case undefined:
      return raw;
    default: {
      warnOnce(
        WARN_ONCE_NAMESPACE,
        `[binding-enhancer] Unknown transform "${String(transform)}"; passing raw value through.`,
      );
      return raw;
    }
  }
}

/**
 * Build the interpolation root used for fallback `{{…}}` templates in
 * `_bind` values. Shape mirrors `DataSourceManager.tsx`'s internal
 * interpolation context:
 *   { context: flowContext, formState, item, <sourceName>: <sourceData> }
 *
 * Side effect: every path reference collected from the template is
 * added to `referencedSources` (after filtering reserved roots) so
 * loading detection covers the fallback case as well as structured
 * declarations.
 */
function buildInterpolationContext(
  ctx: BindingContext,
  referencedSources: Set<string>,
  template: string,
): Record<string, unknown> {
  const providerRoots = activeProviderRoots(ctx);
  const root: Record<string, unknown> = {
    context: ctx.flowContext,
    formState: ctx.formState,
    item: ctx.item,
  };
  if (ctx.route !== undefined) {
    root.route = ctx.route;
  }
  if (ctx.scope !== undefined) {
    root.scope = ctx.scope;
    markPublishedScopeContext(root);
  }
  for (const [name, entry] of Object.entries(ctx.sources)) {
    if (isReservedInterpolationRoot(name, providerRoots)) {
      // Sources named after a reserved root would silently shadow
      // `context` / `formState` / `item` / `event`; skip them so the
      // template grammar stays predictable. DSM also rejects these
      // collisions at registration time, so this is defense in depth.
      warnOnce(
        WARN_ONCE_NAMESPACE,
        `[binding-enhancer] Ignoring data source "${name}" — collides with a reserved interpolation root.`,
      );
      continue;
    }
    root[name] = entry.data;
  }
  try {
    for (const head of extractRootNames(template)) {
      if (head && !isReservedInterpolationRoot(head, providerRoots)) {
        referencedSources.add(head);
      }
    }
  } catch {
    // parse() errors are surfaced by interpolate() below; ignore here
    // so dependency collection does not double-log.
  }
  return root;
}

function collectTemplatePathsFromAction(value: unknown): string[] {
  const declarations = Array.isArray(value) ? value : [value];
  return declarations.flatMap(collectTemplatePathsFromDeclaration);
}

function collectTemplatePathsFromDeclaration(value: unknown): string[] {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return [];
  }
  const decl = value as { params?: unknown };
  if (!decl.params || typeof decl.params !== 'object') {
    return [];
  }
  const paths: string[] = [];
  for (const raw of Object.values(decl.params as Record<string, unknown>)) {
    if (typeof raw !== 'string' || !hasTemplate(raw)) {
      continue;
    }
    try {
      for (const root of extractRootNames(raw)) {
        paths.push(root);
      }
    } catch {
      // Malformed templates surface at dispatch time; skip for
      // dependency collection.
    }
  }
  return paths;
}

function isAnyReferencedSourcePending(
  names: ReadonlySet<string>,
  sources: Readonly<Record<string, ResolvedDataSource>>,
): boolean {
  let pending = false;
  names.forEach((name) => {
    if (pending) return;
    const resolved = sources[name];
    if (resolved !== undefined && resolved.status === 'pending') {
      pending = true;
    }
  });
  return pending;
}

/** Worst-case rank for multi-source status aggregation (lower wins). */
const SOURCE_STATUS_RANK: Readonly<Record<DataSourceStatus, number>> = {
  error: 0,
  pending: 1,
  empty: 2,
  resolved: 3,
};

/** Whether a template in `value` has a path that `ctx` can't resolve yet. */
export function hasMissingTemplatePath(
  value: unknown,
  ctx: BindingContext,
): boolean {
  if (typeof value !== 'string' || !hasTemplate(value)) return false;
  try {
    const resolved = interpolate(
      value,
      buildInterpolationContext(ctx, new Set(), value),
      { onMissing: 'literal' },
    );
    return hasTemplate(resolved);
  } catch {
    return false;
  }
}

/**
 * Aggregate the lifecycle state of every referenced data source.
 * Missing sources resolve to `'error'`. When nothing is referenced,
 * returns `'resolved'` (mirrors `loading: false` for reserved roots).
 */
export function aggregateSourceStatus(
  names: ReadonlySet<string>,
  sources: Readonly<Record<string, ResolvedDataSource>>,
): DataSourceStatus {
  if (names.size === 0) {
    return 'resolved';
  }
  let worst: DataSourceStatus = 'resolved';
  names.forEach((name) => {
    const entry = sources[name];
    const status: DataSourceStatus =
      entry === undefined ? 'error' : entry.status;
    if (SOURCE_STATUS_RANK[status] < SOURCE_STATUS_RANK[worst]) {
      worst = status;
    }
  });
  return worst;
}

const WARN_ONCE_NAMESPACE = 'binding-resolver';

/**
 * Test-only reset for the memoized path cache and warn-once ledger.
 * Keeps unit tests deterministic.
 */
export function __resetBindingResolverCaches(): void {
  pathCache.clear();
  resetWarnOnce(WARN_ONCE_NAMESPACE);
}
